import { IonButton, IonButtons, IonContent, IonFooter, IonHeader, IonIcon, IonItem, IonLabel, IonPage, IonTextarea, IonTitle, IonToolbar } from '@ionic/react';
import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { checkmark, closeCircleOutline, flagOutline, timeOutline, warningOutline } from 'ionicons/icons';
import styles from './DetectionFeedbackModal.module.css';
import { EventClassification } from '../models/DeviceEvent';
import { FeedbackResolution, FeedbackType } from '../models/EventUserFeedback';
import { useQueries } from '@tanstack/react-query';
import { useDeviceEventQuery } from '../query/deviceEventQuery';
import { useEventSummaryQuery } from '../query/eventSummaryQuery';
import { useEventSummaryKeyLocalisedDescriptionQuery } from '../query/eventSummaryKeyLocalisedDescriptionQuery';
import { rfidProfileQueryOptions } from '../query/rfidProfileQuery';
import {
    useDeleteEventUserFeedbackMutation,
    useEventUserFeedbackQuery,
    useUpdateEventUserFeedbackMutation
} from '../query/eventUserFeedbackQuery';
import { getRfidDisplayLabel } from '../utils/rfidDisplay';
import { useComponentTranslation, useModalTranslation } from '../hooks/useTranslation';
import { colorForEventClassification, iconForEventClassification, labelForEventClassification } from '../utils/eventClassificationUtils';
import useUX from '../utils/ux';

const SKIP_SCROLL_ASSIST_ATTRIBUTE = 'data-ionic-skip-scroll-assist';

interface DetectionFeedbackModalProps {
    deviceId: string;
    eventId: number;
    accessToken?: string | null;
    onDismiss: () => void;
}

interface FeedbackOption {
    type: FeedbackType;
    icon: string;
    toneClass: string;
    label: string;
    hint: string;
}

const DetectionFeedbackModal: React.FC<DetectionFeedbackModalProps> = ({ deviceId, eventId, accessToken, onDismiss }) => {
    const { t, i18n } = useModalTranslation();
    const { t: tc } = useComponentTranslation();
    const { presentResult } = useUX();
    const eventQuery = useDeviceEventQuery(deviceId, eventId, accessToken);
    const feedbackQuery = useEventUserFeedbackQuery(deviceId, eventId);
    const feedbackMutation = useUpdateEventUserFeedbackMutation(deviceId, eventId);
    const deleteFeedbackMutation = useDeleteEventUserFeedbackMutation(deviceId, eventId);

    const [selected, setSelected] = useState<FeedbackType | null>(null);
    const [comment, setComment] = useState('');
    const contentRef = useRef<HTMLIonContentElement | null>(null);
    const textareaRef = useRef<HTMLIonTextareaElement | null>(null);

    // Ionic's scroll-assist scrolls this field to a position computed from a
    // pre-keyboard estimate, leaving only its top visible, and its animation
    // overwrites competing scrolls. Opt the field out via Ionic's skip
    // attribute (private API: scroll-assist.js consumes it on every focus,
    // hence it is re-applied on blur) and own the reveal instead: one smooth
    // scroll once the keyboard is fully shown and the webview resized. The
    // textarea is the content's last element, so bottom = fully visible.
    const applySkipScrollAssist = (textarea: HTMLIonTextareaElement | null) => {
        textareaRef.current = textarea;
        textarea?.getInputElement().then(input => input?.setAttribute(SKIP_SCROLL_ASSIST_ATTRIBUTE, 'true'));
    };

    useEffect(() => {
        // ionKeyboardDidShow can fire before the resized webview has
        // re-laid-out the content — scrolling then is a no-op because the
        // content still "fits". Wait until the scroll area's height is stable
        // across two frames and actually has overflow, then scroll once.
        const onKeyboardDidShow = async () => {
            const content = contentRef.current;
            if (!content || !document.activeElement || !content.contains(document.activeElement)) return;
            const scrollEl = await content.getScrollElement();
            let lastHeight = 0;
            for (let frame = 0; frame < 60; frame++) {
                await new Promise(resolve => requestAnimationFrame(resolve));
                const height = scrollEl.clientHeight;
                const overflow = scrollEl.scrollHeight - height - scrollEl.scrollTop;
                if (overflow > 1 && height === lastHeight) {
                    contentRef.current?.scrollToBottom(500);
                    return;
                }
                lastHeight = height;
            }
        };
        window.addEventListener('ionKeyboardDidShow', onKeyboardDidShow);
        return () => window.removeEventListener('ionKeyboardDidShow', onKeyboardDidShow);
    }, []);

    // PWA fallback: no keyboard events (and no scroll-assist) exist there.
    const revealTextarea = () => {
        if (Capacitor.isNativePlatform()) return;
        setTimeout(() => contentRef.current?.scrollToBottom(500), 400);
    };

    const event = eventQuery.data;
    const feedback = feedbackQuery.data;
    const feedbackReviewed = !!feedback && feedback.feedbackResolution != null && feedback.feedbackResolution !== FeedbackResolution.Unresolved;
    const classificationColor = colorForEventClassification(event?.eventEffectiveClassification);
    const classificationLabel = labelForEventClassification(event?.eventClassification, tc);

    // Quote the event's own summary in the "Incorrect Summary" hint, with cat
    // substitution keys ($1…) resolved to plain-text profile names.
    const activeLocale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
    const eventSummary = useEventSummaryQuery(deviceId, eventId, accessToken).data;
    const localisedDescription = useEventSummaryKeyLocalisedDescriptionQuery(eventSummary?.summaryKey || undefined, activeLocale).data?.localisedDescription;
    const rfidCodes = eventSummary?.rfidCodes ?? [];
    const profileQueries = useQueries({ queries: rfidCodes.map(rfidCode => rfidProfileQueryOptions(rfidCode, deviceId)) });
    const summaryText = localisedDescription?.replace(/\$\d+/g, key => {
        const rfidCode = eventSummary?.getRfidCodeFromSubstitutionKey(key);
        if (!rfidCode) return key;
        return getRfidDisplayLabel(rfidCode, profileQueries[rfidCodes.indexOf(rfidCode)]?.data?.label);
    });

    const options: FeedbackOption[] = [
        event?.eventEffectiveClassification === EventClassification.Contraband
            ? {
                type: FeedbackType.FalseAlarm,
                icon: closeCircleOutline,
                toneClass: styles.toneDanger,
                label: t('detection_feedback.false_alarm'),
                hint: t('detection_feedback.false_alarm_hint')
            }
            : {
                type: FeedbackType.MissedContraband,
                icon: warningOutline,
                toneClass: styles.toneDanger,
                label: t('detection_feedback.missed_contraband'),
                hint: t('detection_feedback.missed_contraband_hint')
            },
        {
            type: FeedbackType.IncorrectTimeline,
            icon: timeOutline,
            toneClass: styles.tonePrimary,
            label: t('detection_feedback.incorrect_summary'),
            hint: summaryText
                ? t('detection_feedback.incorrect_summary_hint_quoted', { summary: summaryText })
                : t('detection_feedback.incorrect_summary_hint')
        },
        {
            type: FeedbackType.OtherIssue,
            icon: flagOutline,
            toneClass: styles.toneMedium,
            label: t('detection_feedback.other_issue'),
            hint: t('detection_feedback.other_issue_hint')
        }
    ];

    const otherSelected = selected === FeedbackType.OtherIssue;
    const ready = selected != null && (!otherSelected || comment.trim().length > 0);

    const submit = () => {
        if (!ready || feedbackMutation.isPending) return;
        presentResult(() => feedbackMutation.mutateAsync({
            deviceId,
            eventId,
            feedbackType: selected!,
            feedbackComment: otherSelected ? comment.trim() : undefined
        }));
    };

    const withdraw = () => {
        setSelected(null);
        setComment('');
        presentResult(() => deleteFeedbackMutation.mutateAsync());
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle>{t('detection_feedback.header')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton color="medium" onClick={onDismiss}>
                            {t('detection_feedback.close')}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent ref={contentRef}>
                {feedback && <div className={styles.doneWrapper}>
                    <div className={styles.done}>
                        <div className={styles.doneIcon}><IonIcon icon={checkmark} /></div>
                        <h2 className={styles.doneTitle}>
                            {feedbackReviewed
                                ? t('detection_feedback.reviewed_title')
                                : t('detection_feedback.submitted_title')}
                        </h2>
                        <p className={styles.doneMessage}>
                            {feedbackReviewed
                                ? t('detection_feedback.reviewed_message')
                                : t('detection_feedback.submitted_message')}
                        </p>
                    </div>
                </div>}

                {!feedback && <div className={styles.wrapper}>
                    {event && (
                        <span
                            className={styles.classificationPill}
                            style={{
                                background: `rgba(var(--ion-color-${classificationColor}-rgb), 0.14)`,
                                color: `var(--ion-color-${classificationColor})`
                            }}
                        >
                            <IonIcon icon={iconForEventClassification(event.eventEffectiveClassification)} />
                            {classificationLabel}
                        </span>
                    )}
                    <h2 className={styles.title}>{t('detection_feedback.title')}</h2>
                    <p className={styles.subtitle}>{t('detection_feedback.subtitle', { classification: classificationLabel })}</p>

                    <div className={styles.options}>
                        {options.map(option => {
                            const isSelected = selected === option.type;
                            return (
                                <IonItem
                                    key={option.type}
                                    button
                                    detail={false}
                                    lines="none"
                                    className={isSelected ? `${styles.optionCard} ${styles.optionCardSelected}` : styles.optionCard}
                                    onClick={() => setSelected(option.type)}
                                >
                                    <span slot="start" className={`${styles.optionIcon} ${option.toneClass}`}>
                                        <IonIcon icon={option.icon} />
                                    </span>
                                    <IonLabel className={styles.optionLabel}>
                                        <h3>{option.label}</h3>
                                        <p>{option.hint}</p>
                                    </IonLabel>
                                    {isSelected && <IonIcon slot="end" icon={checkmark} className={styles.optionCheck} />}
                                </IonItem>
                            );
                        })}
                    </div>

                    {otherSelected && (
                        <IonTextarea
                            ref={applySkipScrollAssist}
                            className={styles.otherField}
                            value={comment}
                            onIonFocus={revealTextarea}
                            onIonBlur={() => applySkipScrollAssist(textareaRef.current)}
                            onIonInput={e => setComment(e.detail.value ?? '')}
                            rows={3}
                            autoGrow
                            maxlength={500}
                            counter
                            placeholder={t('detection_feedback.other_placeholder')}
                        />
                    )}
                </div>}
            </IonContent>

            <IonFooter className={`ion-no-border ${styles.footer}`}>
                {feedback && <>
                    <IonButton expand="block" className={styles.submitButton} onClick={onDismiss}>
                        {t('detection_feedback.done')}
                    </IonButton>
                    {!feedbackReviewed && (
                        <IonButton
                            expand="block"
                            fill="clear"
                            color="medium"
                            className={styles.ghostButton}
                            disabled={deleteFeedbackMutation.isPending}
                            onClick={withdraw}
                        >
                            {t('detection_feedback.withdraw')}
                        </IonButton>
                    )}
                </>}
                {!feedback && (
                    <IonButton
                        expand="block"
                        className={styles.submitButton}
                        disabled={!ready || feedbackMutation.isPending}
                        onClick={submit}
                    >
                        {t('detection_feedback.submit')}
                    </IonButton>
                )}
            </IonFooter>
        </IonPage>
    );
};

export default DetectionFeedbackModal;
